import { supabase } from '../config/supabase';
import { AdminUser, UserPermissions } from '../types';

export interface AdminUserFilters {
  search?: string;
  role?: 'superadmin' | 'admin' | 'user';
  is_active?: boolean;
  is_blocked?: boolean;
}

export interface PaginationParams {
  page: number;
  limit: number;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Serviço para gerenciar usuários administrativos
 */
class AdminUsersService {
  /**
   * Busca usuários administrativos com paginação e filtros
   */
  async getAdminUsers(
    filters: AdminUserFilters = {},
    pagination: PaginationParams = { page: 1, limit: 20 }
  ): Promise<AdminUsersResponse> {
    try {
      let query = supabase.from('admin_users').select('*', { count: 'exact' });

      // Aplicar filtros
      if (filters.search) {
        query = query.or(`email.ilike.%${filters.search}%`);
      }

      if (filters.role) {
        query = query.eq('role', filters.role);
      }

      if (filters.is_active !== undefined) {
        query = query.eq('is_active', filters.is_active);
      }

      if (filters.is_blocked !== undefined) {
        query = query.eq('is_blocked', filters.is_blocked);
      }

      // Aplicar paginação
      const offset = (pagination.page - 1) * pagination.limit;
      query = query.range(offset, offset + pagination.limit - 1);

      // Ordenar por última atividade (mais recente primeiro)
      query = query.order('last_login', { ascending: false, nullsFirst: false });

      const { data, error, count } = await query;

      if (error) {
        console.error('Erro ao buscar usuários administrativos:', error);
        throw error;
      }

      const total = count || 0;
      const totalPages = Math.ceil(total / pagination.limit);

      return {
        users: (data || []) as AdminUser[],
        total,
        page: pagination.page,
        limit: pagination.limit,
        totalPages,
      };
    } catch (error) {
      console.error('Erro ao buscar usuários administrativos:', error);
      throw error;
    }
  }

  /**
   * Busca um usuário administrativo por ID
   */
  async getAdminUserById(userId: string): Promise<AdminUser | null> {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Usuário não encontrado
        }
        console.error('Erro ao buscar usuário administrativo:', error);
        throw error;
      }

      return data as AdminUser;
    } catch (error) {
      console.error('Erro ao buscar usuário administrativo:', error);
      return null;
    }
  }

  /**
   * Cria um novo usuário administrativo
   */
  async createAdminUser(userData: {
    email: string;
    role: 'superadmin' | 'admin' | 'user';
    region?: string;
    permissions?: UserPermissions;
    is_active?: boolean;
    is_blocked?: boolean;
  }): Promise<AdminUser> {
    try {
      const defaultPermissions: UserPermissions = {
        can_manage_users: userData.role === 'superadmin',
        can_view_logs: true,
        can_manage_settings: userData.role === 'superadmin',
        can_view_analytics: true,
        can_export_data: userData.role === 'superadmin',
        can_manage_backups: userData.role === 'superadmin',
        can_view_security_logs: userData.role === 'superadmin',
      };

      const { data, error } = await supabase
        .from('admin_users')
        .insert({
          email: userData.email,
          role: userData.role,
          region: userData.region || 'global',
          permissions: userData.permissions || defaultPermissions,
          is_active: userData.is_active !== undefined ? userData.is_active : true,
          is_blocked: userData.is_blocked !== undefined ? userData.is_blocked : false,
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar usuário administrativo:', error);
        throw error;
      }

      return data as AdminUser;
    } catch (error) {
      console.error('Erro ao criar usuário administrativo:', error);
      throw error;
    }
  }

  /**
   * Atualiza um usuário administrativo
   */
  async updateAdminUser(
    userId: string,
    updates: {
      email?: string;
      role?: 'superadmin' | 'admin' | 'user';
      region?: string;
      permissions?: UserPermissions;
      is_active?: boolean;
      is_blocked?: boolean;
    }
  ): Promise<AdminUser> {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar usuário administrativo:', error);
        throw error;
      }

      return data as AdminUser;
    } catch (error) {
      console.error('Erro ao atualizar usuário administrativo:', error);
      throw error;
    }
  }

  /**
   * Atualiza apenas as permissões de um usuário
   */
  async updatePermissions(userId: string, permissions: UserPermissions): Promise<AdminUser> {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .update({ permissions })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar permissões:', error);
        throw error;
      }

      return data as AdminUser;
    } catch (error) {
      console.error('Erro ao atualizar permissões:', error);
      throw error;
    }
  }

  /**
   * Deleta um usuário administrativo
   */
  async deleteAdminUser(userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', userId);

      if (error) {
        console.error('Erro ao deletar usuário administrativo:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Erro ao deletar usuário administrativo:', error);
      return false;
    }
  }
}

export default new AdminUsersService();

